import { createContext, type Dispatch } from "react";
import type { FeedbackDetail } from "../cruise-canvas/interfaces";
import type {
  ActiveDetail,
  ActiveImages,
  ActivityWithFlow,
  ConversationError,
  ConversationState,
  ExampleProject,
  ExtraChatPayload,
  FileInfo,
  GeneratedView,
  Job,
  ServiceFlowRun,
  ShowCaseType,
  Task,
  UploadOptions,
} from "./interfaces";
import type { AIEmployee, Command } from "./ChatCompletions/useChatCompletions";

export interface TaskContextValue {
  conversationId?: string;
  conversationState?: ConversationState;
  tasks: Task[];
  errors: ConversationError[];
  finished?: boolean;
  earlyFinished?: boolean;
  jobMap?: Map<string, Job>;
  flowMap?: Map<string, ServiceFlowRun>;
  activityMap?: Map<string, ActivityWithFlow>;
  workspace?: string;
  previewUrlTemplate?: string;
  replay?: boolean;
  showCases?: ShowCaseType[];
  exampleProjects?: ExampleProject[];
  aiEmployees?: AIEmployee[];
  commands?: Command[];
  uploadOptions?: UploadOptions;

  humanInput: (
    input: string | null,
    action?: string,
    extra?: ExtraChatPayload
  ) => void;
  onShare: () => void;
  onTerminate: () => void;
  supports?: Record<string, boolean>;

  activeExpandedViewJobId: string | null;
  setActiveExpandedViewJobId: Dispatch<React.SetStateAction<string | null>>;
  activeDetail: ActiveDetail | null;
  setActiveDetail: Dispatch<React.SetStateAction<ActiveDetail | null>>;
  subActiveDetail: ActiveDetail | null;
  setSubActiveDetail: Dispatch<React.SetStateAction<ActiveDetail | null>>;

  submittingFeedback: boolean;
  submittedFeedback: boolean;
  onSubmitFeedback: (detail: FeedbackDetail) => void;
  setShowFeedback: Dispatch<React.SetStateAction<boolean>>;

  showJsxEditor?: boolean;
  activeJsxEditorJob?: Job;
  setActiveJsxEditorJob?: Dispatch<React.SetStateAction<Job | undefined>>;
  manuallyUpdatedViews?: Map<string, GeneratedView>;
  updateView?: (jobId: string, view: GeneratedView) => void;
  showFeedbackOnView?: boolean;
  onFeedbackOnView?: (viewId: string) => void;
  feedbackDoneViews?: Set<string>;

  skipToResults?: () => void;
  watchAgain?: () => void;
  tryItOut?: () => void;
  separateInstructions?: boolean;
  activeFile: FileInfo | null;
  setActiveFile: Dispatch<React.SetStateAction<FileInfo | null>>;
  activeImages: ActiveImages | null;
  setActiveImages: Dispatch<React.SetStateAction<ActiveImages | null>>;
}

export const TaskContext = createContext<TaskContextValue>({
  tasks: [],
  errors: [],
  humanInput: () => {},
  onShare: () => {},
  onTerminate: () => {},
  activeExpandedViewJobId: null,
  setActiveExpandedViewJobId: () => {},
  activeDetail: null,
  setActiveDetail: () => {},
  subActiveDetail: null,
  setSubActiveDetail: () => {},
  submittingFeedback: false,
  submittedFeedback: false,
  onSubmitFeedback: () => {},
  setShowFeedback: () => {},
  activeFile: null,
  setActiveFile: () => {},
  activeImages: null,
  setActiveImages: () => {},
});
